import { useMutation } from "@apollo/client/react";
import { DSCircularButton } from "@components/design-system/molecules";
import { DSModal } from "@components/design-system/organisms";
import { faCheck, faTrash } from "@fortawesome/free-solid-svg-icons";
import { useDisplayName } from "@hooks/use-display-name";
import mixpanel from "mixpanel-browser";
import React, { useState } from "react";
import { useLocation } from "react-router";

import MARK_NOTES_EXPORTED from "../../graphql/mutations/MarkNotesExported";
import { useAuth, useWindowDimensions } from "../../hooks";
import Twemoji from "../../Twemoji";
import { alert } from "../common/Alert";
import { SoapEditButton } from "../common/clinical/SoapEditButton";
import ExistingNoteEmailExportButton from "../email/ExistingNoteEmailExportButton";
import CopyFullSOAP from "../notebook/note/CopyFullSOAP";
import ExportToPDF from "../notebook/note/ExportToPDF";
import NoteCreatedAt from "../notebook/note/NoteCreatedAt";
import NotesExpandableSection from "../notebook/note/NotesExpandableSection";
import PatientTitle from "../notebook/PatientTitle";
import { DELETE_NOTE } from "../notes/edit/api/mutations";

const ExportNoteItem = ({
  exportNoteData,
  refetch,
  isForSharedExport = false,
  isAlreadyExported = false,
  activeTab,
}) => {
  const [shouldShowDeleteNoteModal, setShouldShowDeleteNoteModal] =
    useState(false);
  const [modifyNotes] = useMutation(MARK_NOTES_EXPORTED);
  const [deleteNote] = useMutation(DELETE_NOTE);

  const { width } = useWindowDimensions();
  const { userUuid } = useAuth();

  const location = useLocation();

  const ownerDisplayName = useDisplayName(exportNoteData.user, {
    includeLastName: true,
  });

  async function handleDeleteNote(noteUUID) {
    try {
      await deleteNote({ variables: { noteUuid: noteUUID } });
      mixpanel.track("Note Deleted", {
        noteUUID,
        userUuid,
      });
      alert("success", "Note deleted.");
      refetch();
    } catch {
      alert(
        "error",
        "Failed to delete note. Are you trying to delete a note you don't own?",
      );
    } finally {
      setShouldShowDeleteNoteModal(false);
    }
  }

  return (
    <>
      <DSModal
        isOpen={shouldShowDeleteNoteModal}
        onClose={() => setShouldShowDeleteNoteModal(false)}
        title="Delete this note?"
        dataTestId="delete-note-modal"
        actions={[
          {
            label: "Cancel",
            variant: "cancel",
            onClick: () => setShouldShowDeleteNoteModal(false),
          },
          {
            label: "Delete",
            variant: "primary",
            onClick: () => handleDeleteNote(exportNoteData?.uuid),
          },
        ]}
      >
        <h5>
          Are you sure you&apos;d like to{" "}
          <strong>delete this note</strong>?
        </h5>
        <h5 className="text-sm font-semibold mt-4">
          <Twemoji emoji={"📝"} /> {exportNoteData?.title}
        </h5>
        <NoteCreatedAt createdAt={exportNoteData?.createdAt} />
      </DSModal>

      <div
        className="pt-3 px-3 pb-5 rounded-lg border border-secondary bg-secondary shadow-xs"
        data-testid={`export-note-item-${exportNoteData?.title}`}
        data-testid-template={exportNoteData?.jobTypeName}
      >
        <div className="flex items-start">
          <div className="flex flex-1 items-center">
            {!exportNoteData?.patient ? (
              <h5 className="font-bold text-brand text-xl">
                {exportNoteData?.jobTypeName
                  ? exportNoteData?.jobTypeName
                  : "Quicknote"}
              </h5>
            ) : (
              <PatientTitle
                sex={exportNoteData?.patient?.sex}
                species={exportNoteData?.patient?.species}
                name={exportNoteData?.patient?.name}
                patientUUID={exportNoteData?.patient?.uuid}
                lastNameOfOwner={
                  exportNoteData?.patient?.lastNameOfOwner
                }
              />
            )}
            <div className="ml-3">
              <NoteCreatedAt createdAt={exportNoteData?.createdAt} />
            </div>
          </div>
          {width > 500 ? (
            <div className="flex flex-row items-center space-x-2 mb-2">
              {!isForSharedExport && (
                <SoapEditButton note={exportNoteData} />
              )}
              {!isForSharedExport && (
                <>
                  {!isAlreadyExported ? (
                    <DSCircularButton
                      title="Mark note as exported"
                      icon={faCheck}
                      size="sm"
                      variant="primary"
                      onClick={() => {
                        modifyNotes({
                          variables: {
                            noteUuids: [exportNoteData?.uuid],
                            action: "mark_exported",
                          },
                        }).then(() => {
                          if (location.pathname == "/inbox") {
                            refetch({});
                          } else {
                            if (refetch !== null) {
                              refetch({ isApproved: true });
                            }
                          }
                        });
                        alert("success", "Note marked as exported.");
                      }}
                      dataTestId="mark-note-as-exported-button"
                    />
                  ) : (
                    <DSCircularButton
                      title="Delete note"
                      icon={faTrash}
                      size="sm"
                      variant="danger"
                      onClick={() =>
                        setShouldShowDeleteNoteModal(true)
                      }
                      dataTestId="delete-exported-note-button"
                    />
                  )}
                </>
              )}

              {/* {isForSharedExport ? (
                <> */}
              <CopyFullSOAP
                note={exportNoteData}
                refetch={refetch}
                isForSharedExport={isForSharedExport}
              />

              <ExportToPDF
                note={exportNoteData}
                patient={exportNoteData?.patient}
                user={exportNoteData?.user}
                refetch={refetch}
                activeTab={activeTab}
              />
              {/* </>
              ) : (
                <ExportButton note={exportNoteData} size="sm" />
              )} */}
              {!isForSharedExport ? (
                <ExistingNoteEmailExportButton
                  note={exportNoteData}
                  refetch={refetch}
                />
              ) : null}
            </div>
          ) : null}
        </div>

        <div className="flex flex-row justify-between w-full flex-1 items-end">
          {exportNoteData?.title ? (
            <h4
              className="font-semibold text-sm text-secondary"
              data-testid="note-title"
            >
              <Twemoji emoji="📝" /> {exportNoteData?.title}
            </h4>
          ) : null}
          {exportNoteData?.user && (
            <div className="text-xs text-secondary">
              {ownerDisplayName}
            </div>
          )}
        </div>

        <NotesExpandableSection data={exportNoteData} />
      </div>
    </>
  );
};

export default ExportNoteItem;
